import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { FormFieldControl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import { ValidateField } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import format from 'date-fns/format/index.js';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import {
  SpoItemIntlType,
  SpoItemType,
  StockInputValue,
  StockInputValue_Edit_Option_Intl,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/types.ts';
import {
  spoItemSchema,
  spoItemIntlSchema,
  spoOptionSchema,
  spoOptionIntlSchema,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/spo.ts';
import {
  SpoItem,
  SpoItemIntl,
  SpoOption,
  SpoIntlOption,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/InputOption.ts';
import { getAllCombinations } from '../../../../../../../shared/utils/getAllCombinations.ts';
import { groupBy, isNumber } from '@fxts/core';

export const subscribeStore = <T extends MODE>(
  store: ReturnType<typeof createCreatorProductStore<T>>,
  inputMap: Partial<Record<ValidateField, FormFieldControl>>,
) => {
  store.subscribeState(
    (state) => state.errors,
    (errors) => {
      const isValidFieldKey = (k: string): k is ValidateField => {
        return k in errors;
      };

      Object.entries(inputMap).forEach(([name, view]) => {
        if (isValidFieldKey(name) && errors[name]) {
          view.setError(true, errors[name], name);
        } else {
          view.setError(false, undefined, name);
        }
      });
    },
  );
};

export const createSubscribeStoreList = () => new Set<ValidateField>();

export const formatDateField = (date?: Date | null): string | null =>
  date ? format(date, 'yyyy-MM-dd HH:mm') : null;

export const updateSpoItems = (
  existing_spo_items: SpoItemType[],
  stock_values: StockInputValue[],
): SpoItemType[] => {
  return stock_values.map((stockValue, index) => {
    const existingItem = existing_spo_items[index];
    const origin_quantity = isNumber(existingItem?._?.spo_item_stock?.origin_quantity)
      ? existingItem?._?.spo_item_stock?.origin_quantity
      : existingItem?._?.spo_item_stock?.quantity;

    const baseItem = {
      // 기존 아이템의 모든 속성을 먼저 복사
      ...(existingItem || {}),

      // 새로운 값으로 업데이트할 속성들
      price: 'price' in stockValue ? stockValue.price : existingItem?.price || 0,

      // sold_quantity가 있으면 업데이트
      ...('sold_quantity' in stockValue ? { sold_quantity: stockValue.sold_quantity } : {}),

      _: {
        // 기존 _ 속성 보존
        ...(existingItem?._ || {}),
        sku: {
          // 기존 spo_item_stock 속성 보존
          ...(existingItem?._?.sku || {}),
          // sku.nickname 있으면 업데이트
          ...(stockValue.sku ? { nickname: stockValue.sku } : { nickname: null }),
        },
        spo_item_stock: {
          // 기존 spo_item_stock 속성 보존
          ...(existingItem?._?.spo_item_stock || {}),
          origin_quantity,
          quantity: stockValue.stock,
          plus_minus_quantity:
            existingItem?._?.spo_item_stock.quantity === stockValue.stock
              ? existingItem?._?.spo_item_stock?.plus_minus_quantity
              : stockValue.stock - (origin_quantity || 0),
        },
      },
    };

    return spoItemSchema.parse(baseItem);
  });
};

export const updateSpoIntlItems = (
  existing_spo_items: SpoItemIntlType[],
  stock_values: StockInputValue_Edit_Option_Intl[],
): SpoItemIntlType[] => {
  return stock_values.map((stockValue, index) => {
    const existingItem = existing_spo_items[index];
    const origin_quantity = isNumber(existingItem?._?.spo_item_stock?.origin_quantity)
      ? existingItem?._?.spo_item_stock?.origin_quantity
      : existingItem?._?.spo_item_stock?.quantity;

    const baseItem = {
      // 기존 아이템의 모든 속성을 먼저 복사
      ...(existingItem || {}),

      // 새로운 값으로 업데이트할 속성들
      ...(stockValue.sku || stockValue.volume
        ? {
            sku: {
              ...(stockValue.sku ? { nickname: stockValue.sku } : {}),
              ...(stockValue.volume ? stockValue.volume : {}),
            },
          }
        : {}),
      price: 'price' in stockValue ? stockValue.price : existingItem?.price || 0,

      // sold_quantity가 있으면 업데이트
      ...('sold_quantity' in stockValue ? { sold_quantity: stockValue.sold_quantity } : {}),

      _: {
        // 기존 _ 속성 보존
        ...(existingItem?._ || {}),

        sku: {
          // 기존 spo_item_stock 속성 보존
          ...(existingItem?._?.sku || {}),
          // sku.nickname 있으면 업데이트
          ...(stockValue.sku || stockValue.volume
            ? {
                ...(stockValue.sku ? { nickname: stockValue.sku } : { nickname: null }),
                ...(stockValue.volume ? stockValue.volume : {}),
              }
            : {}),
        },

        spo_item_stock: {
          // 기존 spo_item_stock 속성 보존
          ...(existingItem?._?.spo_item_stock || {}),
          origin_quantity,
          quantity: stockValue.stock,
          plus_minus_quantity:
            existingItem?._?.spo_item_stock.quantity === stockValue.stock
              ? existingItem?._?.spo_item_stock?.plus_minus_quantity
              : stockValue.stock - (origin_quantity || 0),
        },
      },
    };

    return spoItemIntlSchema.parse(baseItem);
  });
};

/* spo_options 의 모든 조합으로 spo_items 생성 */
export const toSpoItems = (spo_options: SpoOption[]): SpoItem[] => {
  if (spo_options.length === 0) return [];

  // 각 옵션을 group_name, option_name 형태로 변환
  const optionCombinations = spo_options.map((option) =>
    option._.spo_option_values.map((value) => ({
      group_name: option.name,
      option_name: value.name,
    })),
  );

  // 모든 조합 생성
  const allCombinations = getAllCombinations(optionCombinations);

  // 각 조합을 spo_item 구조로 변환
  return allCombinations.map((combination) => {
    const rawItem = {
      price: 0,
      sku: { name: '', nickname: '' },
      _: { spo_item_stock: { quantity: 0 } },
      sold_quantity: 0,
      group_names_option_names: combination,
    };

    return spoItemSchema.parse(rawItem);
  });
};

export const toSpoItemsIntl = (spo_intl_options: SpoIntlOption[]): SpoItemIntl[] => {
  if (spo_intl_options.length === 0) return [];

  const optionCombinations = spo_intl_options.map((option) =>
    option._.spo_option_values.map((value) => ({
      group_name: option.name,
      group_name_en: option.name_en,
      group_name_jp: option.name_jp,
      option_name: value.name,
      option_name_en: value.name_en,
      option_name_jp: value.name_jp,
    })),
  );

  const allCombinations = getAllCombinations(optionCombinations);

  return allCombinations.map((combination) => {
    const rawItem = {
      price: 0,
      sku: { name: '', nickname: '' },
      _: { spo_item_stock: { quantity: 0 } },
      sold_quantity: 0,
      group_names_option_names: combination,
    };

    return spoItemIntlSchema.parse(rawItem);
  });
};

export const extractSpoOptions = (spo_items: SpoItem[], original_spo_options: SpoOption[]): SpoOption[] => {
  if (spo_items.length === 0) return [];

  const flattened = spo_items.flatMap((item) => item.group_names_option_names);
  const grouped = groupBy((group_names_option_name) => group_names_option_name!.group_name, flattened);

  // 원본 옵션 순서 맵 생성
  const originalOptionOrder = new Map(original_spo_options.map((option, index) => [option.name, index]));

  const originalValueOrder = new Map(
    original_spo_options.map((option) => [
      option.name,
      new Map(option._.spo_option_values.map((value, index) => [value.name, index])),
    ]),
  );

  return (
    Object.entries(grouped)
      // 원본 옵션 순서대로 정렬
      .sort(([a], [b]) => {
        const orderA = originalOptionOrder.get(a) ?? Infinity;
        const orderB = originalOptionOrder.get(b) ?? Infinity;
        return orderA - orderB;
      })
      .map(([groupName, items]) => {
        const valueOrder = originalValueOrder.get(groupName);

        const rawOption = {
          name: groupName,
          _: {
            spo_option_values: [
              ...new Set(items.map((group_names_option_name) => group_names_option_name!.option_name)),
            ]
              // 원본 옵션값 순서대로 정렬
              .sort((a, b) => {
                const orderA = valueOrder?.get(a) ?? Infinity;
                const orderB = valueOrder?.get(b) ?? Infinity;
                return orderA - orderB;
              })
              .map((name) => ({ name })),
          },
        };

        return spoOptionSchema.parse(rawOption);
      })
  );
};

export const extractSpoIntlOptions = (
  spo_items: SpoItemIntl[],
  original_spo_options: SpoIntlOption[],
): SpoIntlOption[] => {
  if (spo_items.length === 0) return [];

  const flattened = spo_items.flatMap((item) => item.group_names_option_names);
  const grouped = groupBy((group_names_option_name) => group_names_option_name!.group_name, flattened);

  // 원본 옵션 순서 맵 생성
  const originalOptionOrder = new Map(original_spo_options.map((option, index) => [option.name, index]));

  const originalValueOrder = new Map(
    original_spo_options.map((option) => [
      option.name,
      new Map(option._.spo_option_values.map((value, index) => [value.name, index])),
    ]),
  );

  return (
    Object.entries(grouped)
      // 원본 옵션 순서대로 정렬
      .sort(([a], [b]) => {
        const orderA = originalOptionOrder.get(a) ?? Infinity;
        const orderB = originalOptionOrder.get(b) ?? Infinity;
        return orderA - orderB;
      })
      .map(([groupName, items]) => {
        const valueOrder = originalValueOrder.get(groupName);

        // 첫 번째 아이템에서 다국어 정보 추출
        const firstItem = items[0];

        const rawOption = {
          name: groupName,
          name_en: firstItem?.group_name_en || groupName,
          name_jp: firstItem?.group_name_jp || groupName,
          _: {
            spo_option_values: [
              ...new Set(items.map((group_names_option_name) => group_names_option_name!.option_name)),
            ]
              // 원본 옵션값 순서대로 정렬
              .sort((a, b) => {
                const orderA = valueOrder?.get(a) ?? Infinity;
                const orderB = valueOrder?.get(b) ?? Infinity;
                return orderA - orderB;
              })
              .map((optionName) => {
                // 해당 옵션 이름에 대한 다국어 정보 찾기
                const itemWithThisOption = items.find((item) => item?.option_name === optionName);

                return {
                  name: optionName,
                  name_en: itemWithThisOption?.option_name_en || optionName,
                  name_jp: itemWithThisOption?.option_name_jp || optionName,
                };
              }),
          },
        };

        return spoOptionIntlSchema.parse(rawOption);
      })
  );
};
